/**
 * Helpers för konto-aktivering via deep-link (S-13-05 + Lyktan-side,
 * ord-disciplin S-13-07).
 *
 * Genererar `harden://activate?pilot_id=...&code=...`-URL och
 * pre-formaterad SMS-text som admin kan kopiera och skicka till
 * deltagaren.
 *
 * **S-13-07 — host-rename:**
 * Tidigare format `harden://pilot-activate?...` är borttaget från ny
 * output men lever kvar som accepterat host-namn i Android- och iOS-
 * parsern (för utestående aktiveringslänkar med kort TTL).
 *
 * **Säkerhet:**
 *  - URL-encoding skyddar mot specialtecken som annars skulle kunna
 *    bryta deep-link-parsern på Android/iOS
 *  - Strict format: bara två parametrar, inga andra fält
 *  - Innehåller ingen Compuna-secret (konto-id + activation_code är
 *    redan låg-risk-credential med kort TTL)
 *
 * **Format-not:** Android- och iOS-app:n parsar bara `pilot_id` och
 * `code` — ändringar i parameter-ordning eller URL-format kräver
 * synk:ad uppdatering i `android/app/.../onboarding/PilotActivationDeepLink.kt`
 * och `ios/HardenApp/Onboarding/PilotActivationDeepLink.swift`.
 */

const SCHEME_AND_HOST = 'harden://activate';

export function buildPilotActivationDeepLink(
  pilotId: string,
  activationCode: string,
): string {
  const params = new URLSearchParams({
    pilot_id: pilotId,
    code: activationCode,
  });
  return `${SCHEME_AND_HOST}?${params.toString()}`;
}

/**
 * Pre-formaterad SMS-text som admin kan kopiera och skicka. Kort
 * format så det inte blir flera segment (max 160 tecken för 1 SMS).
 *
 * Räkning för en typisk pilot-id (8 tecken) + activation-code (8-12
 * tecken) ger ~140-150 tecken — inom 1 SMS-segment. Längre koder kan
 * spilla över till 2 segment (159+ tecken) vilket är OK men dyrare
 * vid Twilio/46elks-utskick.
 */
export function buildPilotActivationSmsText(deepLinkUrl: string): string {
  return `Välkommen till Härden! Tryck här för att aktivera ditt aktiveringskonto: ${deepLinkUrl}`;
}

/**
 * Längre välkomst-text för e-post (eller Signal/iMessage). Inkluderar
 * Play Store-instruktioner för Internal Testing-fasen och kontakt.
 *
 * Compuna kan klistra in i e-post-klienten som är configured (Apple
 * Mail, Outlook, Gmail) och anpassa per pilot-deltagare.
 */
export function buildPilotActivationEmailBody(
  deepLinkUrl: string,
  recipientFirstName?: string,
): string {
  const greeting = recipientFirstName ? `Hej ${recipientFirstName}!` : 'Hej!';
  return [
    greeting,
    '',
    'Välkommen som deltagare i Härden — tack för att du vill bidra till att göra Hisings Backa tryggare.',
    '',
    'För att komma igång:',
    '',
    '1. Installera Härden via Play Store-länken jag mailat separat (du behöver acceptera Internal Testing-inbjudan först).',
    '',
    '2. När appen är installerad — tryck här för att aktivera ditt konto:',
    '',
    deepLinkUrl,
    '',
    'Länken öppnar Härden direkt och fyller i ditt konto-id automatiskt. Tryck "Aktivera" för att slutföra.',
    '',
    'Frågor? Svara på det här mailet eller hör av dig till jonas@compuna.se.',
    '',
    'Ha det fint!',
    'Compuna AB',
  ].join('\n');
}
